// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: native code calls the API and gateway on its own (calls on a locked
// phone, notification reply actions, Live Activity buttons, token
// registration), so it needs the current session. Mirror it on every change.

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {NativePlugins, type NativeSession} from '@app/features/platform/native_mobile/NativeMobileBridge';
import SessionManager from '@app/features/platform/state/AuthSession';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {reaction} from 'mobx';

const logger = new Logger('NativeMobileSession');

function absolute(url: string, protocolFallback?: 'wss:'): string {
	if (!url) return '';
	try {
		const resolved = new URL(url, window.location.origin);
		if (protocolFallback && (resolved.protocol === 'http:' || resolved.protocol === 'https:')) {
			resolved.protocol = resolved.protocol === 'http:' ? 'ws:' : 'wss:';
		}
		return resolved.toString().replace(/\/$/, '');
	} catch {
		return url;
	}
}

function buildSession(): NativeSession {
	const apiEndpoint = absolute(RuntimeConfig.apiEndpoint || '/api');
	return {
		token: SessionManager.token,
		userId: SessionManager.userId,
		apiBase: `${apiEndpoint}/v${RuntimeConfig.apiCodeVersion}`,
		gatewayUrl: absolute(RuntimeConfig.gatewayEndpoint, 'wss:'),
		apiVersion: RuntimeConfig.apiCodeVersion,
		mediaBase: RuntimeConfig.mediaEndpoint ? absolute(RuntimeConfig.mediaEndpoint) : null,
	};
}

function sessionKey(session: NativeSession): string {
	return JSON.stringify(session);
}

let lastSentKey: string | null = null;

export async function syncNativeSessionNow(): Promise<void> {
	// A cold web-view starts with an empty session until storage hydration finishes.
	// Sending that placeholder to native would log out push handling and erase its cache.
	if (!SessionManager.isInitialized) return;
	const plugin = NativePlugins.native();
	if (!plugin) return;
	const session = buildSession();
	if (session.token && (!session.gatewayUrl || !session.userId)) return;
	const key = sessionKey(session);
	if (key === lastSentKey) return;
	try {
		await plugin.setSession(session);
		lastSentKey = key;
	} catch (error) {
		logger.warn('Failed to hand the session to native', error);
	}
}

export function startNativeSessionSync(): () => void {
	if (!NativePlugins.native()) return () => {};
	void syncNativeSessionNow();
	return reaction(
		() => `${SessionManager.isInitialized}:${sessionKey(buildSession())}`,
		() => void syncNativeSessionNow(),
	);
}
